import type { ResolvedRemovalOrder } from "../types/removal";
import { round2 } from "utils/fixedAmountDiscountAllocation";
import {
  findCatalogRowById,
  readResolveRequired,
  resolveTaxableFromSources,
} from "../utils/readResolveTaxable";

/** PATCH body for signage removal live-tax / edit save from last resolve response. */
export function buildRemovalPatchFromResolved(
  resolved: ResolvedRemovalOrder,
  args: {
    version?: number | string | null;
    removalCost: number;
    taxStatus?: string;
    existingFeeRows?: unknown[] | null;
    specialFeeCatalog?: unknown[] | null;
  }
): Record<string, unknown> {
  const feeRows = (resolved.specialfee || []).map((f) => {
    const id = String(f.specialfee_id || "");
    const existingRow = findCatalogRowById(args.existingFeeRows, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    const catalogRow = findCatalogRowById(args.specialFeeCatalog, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    const taxable = resolveTaxableFromSources(
      [existingRow, catalogRow, f as Record<string, unknown>],
      ["specialfee_taxable", "taxable", "is_taxable"],
      true
    );
    return {
      specialfee_id: id,
      specialfee_name: String(f.specialfee_name || ""),
      specialfee_amount: Number(f.specialfee_amount || 0),
      specialfee_tax_percentage: Number(f.specialfee_tax_percentage || 0),
      specialfee_tax_amount: Number(f.specialfee_tax_amount || 0),
      specialfee_taxable: taxable,
      required: readResolveRequired(existingRow) || f.required === true,
    };
  });

  const removalTax = Number(resolved.removal_tax_amount || 0);
  const removalTaxPct = String(resolved.removal_tax_percentage ?? "");
  const overallTax = Number(resolved.over_all_tax_amount || 0);
  const overallTaxPct = String(resolved.over_all_tax_percentage ?? "");
  const subtotalAmount = Number(resolved.subtotal_amount ?? args.removalCost);
  const totalAmount = Number(resolved.total_amount ?? 0);
  const orderDiscountAmount = Number(resolved.discount_amount ?? 0);

  return {
    version: args.version,
    subtotal_amount: subtotalAmount,
    total_amount: totalAmount,
    removal_cost: Number(resolved.removal_cost ?? args.removalCost),
    removal_tax_amount: removalTax,
    removal_tax_percentage: removalTaxPct,
    specialfee: feeRows,
    tax_status: args.taxStatus ?? resolved.tax_status ?? "draft",
    over_all_tax_amount: overallTax,
    over_all_tax_percentage: overallTaxPct,
    discount_amount: orderDiscountAmount,
    discount_code: String(resolved.discount_code ?? "").trim(),
  };
}

export function removalOverallTaxFromResolved(resolved: ResolvedRemovalOrder | null | undefined): number {
  if (!resolved) return 0;
  const removalTax = Number(resolved.removal_tax_amount || 0);
  const feeTax = (resolved.specialfee || []).reduce(
    (s, f) => s + Number(f.specialfee_tax_amount || 0),
    0
  );
  return round2(Number(resolved.over_all_tax_amount ?? removalTax + feeTax));
}
